/**
 * The aircraft register in Teslyn Cloud (MB's R41, decision 506; help `cloud.aircraft`). Every staff member reads the
 * tenant's aircraft; RLS lets the staff roles write the aircraft row, and the database's `aircraft_register_guard`
 * lets only tenant admins and managers change the register settings (engines, engine starts, oil uplift, dipstick unit, fuel unit,
 * aircraft type). An aircraft's tracker devices are the tracker assets whose `aircraft_id` is the aircraft; the
 * devices to choose from are those the tenant's tracking accounts have reported (`tracker_seen_devices`) and the
 * assets (docs/specs/cloud/aircraft-trackers.md). Aircraft are imported from TJR files with `importAircraft`
 * (MB's R79; docs/specs/cloud/tjr-register-import.md).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { assetForLink, registerNamesTenant, registerRowFromDb } from '@teslyn/domain';
import type {
  Aircraft,
  AircraftImportInsert,
  FleetProviderId,
  RegisterTypeRow,
  SeenDevice,
  TrackerAsset,
  TrackerChoice,
} from '@teslyn/domain';

// the register's columns, the photo set in Teslyn Cloud included (decision 598)
import { AIRCRAFT_COLUMNS } from './photoApi.ts';

function fail(error: { readonly message: string } | null, what: string): void {
  if (error) throw new Error(`${what}: ${error.message}`);
}

export async function loadAircraft(sb: SupabaseClient, tenantId: string): Promise<Aircraft[]> {
  const r = await sb
    .from('aircraft')
    .select(AIRCRAFT_COLUMNS)
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .order('registration');
  fail(r.error, 'The aircraft could not be read');
  return (r.data ?? []) as Aircraft[];
}

export async function loadTrackers(sb: SupabaseClient, tenantId: string): Promise<TrackerAsset[]> {
  const r = await sb
    .from('tracker_asset')
    .select(
      'id, tenant_id, provider, account_label, device_id, label, registration, icao_hex, kind, colour, fleet_group, aircraft_id, hidden, deleted_at',
    )
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .order('label');
  fail(r.error, 'The tracker devices could not be read');
  return (r.data ?? []) as TrackerAsset[];
}

/** Save the register settings of an aircraft (only its settings columns are sent). */
export async function saveAircraftSettings(
  sb: SupabaseClient,
  a: Aircraft,
  scope: 'all' | 'open' = 'all',
): Promise<Aircraft> {
  // the older settings, open to all staff (decision 537 point 4 B); the five of batch 3 only for 'all'
  const open = {
    fuel_type: a.fuel_type ?? null,
    mr_interval_h: a.mr_interval_h,
    max_flight_h: a.max_flight_h ?? null,
    fuel_flow_lph: a.fuel_flow_lph,
    // decision 547: an aircraft imported from TJR with its type unknown gets it filled in here
    type_name: a.type_name ?? '',
    // decision 863 (6 B): the JSA's empty weight, MTOW and reserve, kept in the register
    empty_weight_kg: a.empty_weight_kg ?? null,
    mtow_kg: a.mtow_kg ?? null,
    reserve_min: a.reserve_min ?? null,
  };
  const r = await sb
    .from('aircraft')
    .update(
      scope === 'open'
        ? open
        : {
            airframe: a.airframe ?? null,
            engines: a.engines ?? null,
            engine_starts_tracked: a.engine_starts_tracked ?? null,
            oil_uplift_tracked: a.oil_uplift_tracked ?? null,
            fuel_unit: a.fuel_unit ?? null,
            // decision 920: the oil dipstick's unit, a register setting like the fuel unit
            oil_dipstick_unit: a.oil_dipstick_unit ?? null,
            ...open,
          },
    )
    .eq('id', a.id)
    .select(AIRCRAFT_COLUMNS);
  if (r.error && r.error.code === '42501')
    throw new Error('Only a tenant admin or a manager may change the aircraft register settings.');
  fail(r.error, 'The aircraft was not saved');
  const saved = (r.data ?? [])[0] as Aircraft | undefined;
  if (!saved) throw new Error('The aircraft was not saved: you may not change it.');
  return saved;
}

/** The devices the tenant's tracking accounts reported in the positions kept (48 hours), one row each. */
export async function loadSeenDevices(sb: SupabaseClient, tenantId: string): Promise<SeenDevice[]> {
  const r = await sb.rpc('tracker_seen_devices', { p_tenant: tenantId });
  fail(r.error, 'The tracker devices could not be read');
  return ((r.data ?? []) as Record<string, unknown>[]).map((d) => ({
    provider: String(d.provider) as FleetProviderId,
    account_label: String(d.account_label ?? ''),
    device_id: String(d.device_id),
    label: String(d.label ?? ''),
    registration: String(d.registration ?? ''),
    kind: (d.kind as SeenDevice['kind']) ?? null,
    fleet_group: String(d.fleet_group ?? ''),
    last_seen: (d.last_seen as string | null) ?? null,
  }));
}

const trackerRefused = (e: { readonly code?: string; readonly message: string } | null): void => {
  if (e?.code === '42501') throw new Error('Only a tenant admin or a manager may link tracker devices.');
  fail(e, 'The tracker device was not linked');
};

/**
 * Link a device to an aircraft: its asset gets the aircraft's id, or a device with no asset gets one (provider,
 * account, device id, name and registration from the device). A device linked to another aircraft moves.
 */
export async function linkTracker(
  sb: SupabaseClient,
  tenantId: string,
  aircraft: Pick<Aircraft, 'id' | 'registration' | 'airframe' | 'type_name'>,
  choice: TrackerChoice,
): Promise<void> {
  if (choice.asset) {
    const r = await sb
      .from('tracker_asset')
      .update({ aircraft_id: aircraft.id })
      .eq('id', choice.asset.id)
      .select('id');
    trackerRefused(r.error);
    if ((r.data ?? []).length === 0)
      throw new Error('Only a tenant admin or a manager may link tracker devices.');
    return;
  }
  const r = await sb
    .from('tracker_asset')
    .insert(assetForLink(choice.device, tenantId, aircraft))
    .select('id');
  trackerRefused(r.error);
}

/** Take a tracker device off its aircraft (the asset stays, with its name and colour). */
export async function unlinkTracker(sb: SupabaseClient, assetId: string): Promise<void> {
  const r = await sb.from('tracker_asset').update({ aircraft_id: null }).eq('id', assetId).select('id');
  trackerRefused(r.error);
}

/**
 * The national registers' answer for TJR's registrations (`aircraft_register_lookup`), with whether each names the
 * organisation as holder or operator. Empty when the lookup is refused or fails: the import then takes types from
 * TJR's jobs only.
 */
export async function lookupRegisterTypes(
  sb: SupabaseClient,
  tenantId: string,
  tenantName: string,
  marks: readonly string[],
): Promise<RegisterTypeRow[]> {
  const asked = marks.filter((m) => /^[A-Z0-9]{1,2}-?[A-Z0-9]{1,5}$/.test(m)).slice(0, 100);
  if (asked.length === 0) return [];
  const r = await sb.rpc('aircraft_register_lookup', { p_tenant: tenantId, p_marks: asked, p_hexes: [] });
  if (r.error) return [];
  const rows = ((r.data as { rows?: Record<string, unknown>[] } | null)?.rows ?? []).map(
    (d) => registerRowFromDb(d).row,
  );
  return rows.map((row) => ({
    mark: row.mark,
    manufacturer: row.manufacturer,
    model: row.model,
    icao_type: row.icao_type,
    airframe: row.airframe,
    mtow_kg: row.mtow_kg,
    names_tenant: registerNamesTenant(row, tenantName),
  }));
}

/** Add the ticked aircraft of a TJR import in one insert; returns how many were added. */
export async function importAircraft(
  sb: SupabaseClient,
  rows: readonly AircraftImportInsert[],
): Promise<number> {
  if (rows.length === 0) return 0;
  const r = await sb
    .from('aircraft')
    .insert(rows.map((x) => ({ ...x })))
    .select('id');
  fail(r.error, 'The aircraft were not imported');
  return (r.data ?? []).length;
}
